import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import axios from "axios";
import {
  X, Calendar, Clock, ChevronLeft, ChevronRight,
  Send, AlertCircle, CheckCircle2,
} from "lucide-react";
import { toast } from "react-toastify";
import Config from '../../../../src/utils/Config';

const BASE_URL = Config.baseUrl;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DAYS = ["Su","Mo","Tu","We","Th","Fr","Sa"];

const pad = (n) => String(n).padStart(2, "0");

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

/** Formats to "YYYY-MM-DD HH:MM:SS" as required by the API */
function formatScheduledAt(date, hour, minute, ampm) {
  let h = parseInt(hour, 10);
  if (ampm === "PM" && h !== 12) h += 12;
  if (ampm === "AM" && h === 12) h = 0;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(h)}:${pad(minute)}:00`;
}

// ─── Scroll Drum ──────────────────────────────────────────────────────────────
const ITEM_H = 36;

const Drum = ({ items, value, onChange }) => {
  const ref = useRef(null);
  const isScrolling = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const idx = items.indexOf(value);
    if (idx >= 0) el.scrollTop = idx * ITEM_H;
  }, [value, items]);

  const handleScroll = (e) => {
    if (isScrolling.current) return;
    const idx = Math.round(e.target.scrollTop / ITEM_H);
    const clamped = Math.max(0, Math.min(idx, items.length - 1));
    if (items[clamped] !== value) onChange(items[clamped]);
  };

  const handleClick = (item, idx) => {
    onChange(item);
    if (ref.current) {
      isScrolling.current = true;
      ref.current.scrollTo({ top: idx * ITEM_H, behavior: "smooth" });
      setTimeout(() => { isScrolling.current = false; }, 300);
    }
  };

  return (
    <div className="relative h-[108px] overflow-hidden rounded-xl border border-gray-100 bg-white">
      <div className="pointer-events-none absolute top-0 left-0 right-0 h-8 z-10 bg-gradient-to-b from-white to-transparent" />
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 z-10 bg-gradient-to-t from-white to-transparent" />
      <div className="pointer-events-none absolute top-[36px] left-1 right-1 h-9 z-0 rounded-lg border-y border-red-100 bg-red-50/60" />

      <div
        ref={ref}
        onScroll={handleScroll}
        className="h-full overflow-y-scroll scrollbar-hide"
        style={{ scrollSnapType: "y mandatory", WebkitOverflowScrolling: "touch" }}
      >
        <div style={{ height: ITEM_H }} />
        {items.map((item, idx) => (
          <div
            key={item}
            onClick={() => handleClick(item, idx)}
            style={{ height: ITEM_H, scrollSnapAlign: "start" }}
            className={`flex items-center justify-center cursor-pointer text-sm font-medium transition-colors select-none ${
              value === item
                ? "text-red-600 font-bold"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            {item}
          </div>
        ))}
        <div style={{ height: ITEM_H }} />
      </div>
    </div>
  );
};

// ─── Time Picker ──────────────────────────────────────────────────────────────
const HOURS = Array.from({ length: 12 }, (_, i) => pad(i + 1));
const MINUTES = ["00", "15", "30", "45"];

const TimePicker = ({ hour, minute, ampm, onHourChange, onMinuteChange, onAmpmChange }) => (
  <div className="flex items-center gap-3">
    {/* Hour drum */}
    <div className="flex-1">
      <div className="text-[10px] text-center text-gray-400 mb-1 font-medium uppercase tracking-wide">Hour</div>
      <Drum items={HOURS} value={hour} onChange={onHourChange} />
    </div>

    {/* Minute drum */}
    <div className="flex-1">
      <div className="text-[10px] text-center text-gray-400 mb-1 font-medium uppercase tracking-wide">Min</div>
      <Drum items={MINUTES} value={minute} onChange={onMinuteChange} />
    </div>

    {/* AM / PM */}
    <div className="flex flex-col gap-1.5 flex-shrink-0">
      <div className="text-[10px] text-center text-gray-400 mb-1 font-medium uppercase tracking-wide invisible">·</div>
      {["AM", "PM"].map((p) => (
        <button
          key={p}
          onClick={() => onAmpmChange(p)}
          className={`px-3 py-2 rounded-xl text-xs font-bold transition-all ${
            ampm === p
              ? "bg-red-600 text-white shadow-sm shadow-red-200"
              : "bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-600"
          }`}
        >
          {p}
        </button>
      ))}
    </div>
  </div>
);

// ─── Mini Calendar ────────────────────────────────────────────────────────────
const MiniCalendar = ({ selectedDate, onChange, minDate }) => {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay   = getFirstDayOfMonth(viewYear, viewMonth);
  const cells = Array(firstDay).fill(null).concat(
    Array.from({ length: daysInMonth }, (_, i) => i + 1)
  );

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const isDisabled = (day) => {
    if (!day) return true;
    const d = new Date(viewYear, viewMonth, day);
    d.setHours(0, 0, 0, 0);
    const min = new Date(minDate);
    min.setHours(0, 0, 0, 0);
    return d < min;
  };

  const isSelected = (day) =>
    day && selectedDate &&
    selectedDate.getFullYear() === viewYear &&
    selectedDate.getMonth()   === viewMonth &&
    selectedDate.getDate()    === day;

  const isToday = (day) =>
    day &&
    today.getFullYear() === viewYear &&
    today.getMonth()    === viewMonth &&
    today.getDate()     === day;

  return (
    <div className="select-none">
      <div className="flex items-center justify-between mb-3">
        <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 transition-colors">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-semibold text-gray-800">{MONTHS[viewMonth]} {viewYear}</span>
        <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 transition-colors">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 mb-1">
        {DAYS.map(d => (
          <div key={d} className="text-center text-[10px] font-semibold text-gray-400 py-1">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((day, idx) => (
          <button
            key={idx}
            disabled={!day || isDisabled(day)}
            onClick={() => day && !isDisabled(day) && onChange(new Date(viewYear, viewMonth, day))}
            className={`
              aspect-square flex items-center justify-center rounded-lg text-xs font-medium transition-all
              ${!day ? "invisible pointer-events-none" : ""}
              ${isDisabled(day) ? "text-gray-300 cursor-not-allowed" : "cursor-pointer"}
              ${isSelected(day)
                ? "bg-red-600 text-white shadow-sm shadow-red-200"
                : isToday(day) && !isDisabled(day)
                  ? "bg-red-50 text-red-600 border border-red-200"
                  : !isDisabled(day)
                    ? "text-gray-700 hover:bg-red-50 hover:text-red-600"
                    : ""
              }
            `}
          >
            {day}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────
const ScheduleCampaignModal = ({
  isOpen,
  onClose,
  selectedGroups = [],
  selectedSenderId = "",
  message = "",
}) => {
  const today = new Date();

  const [campaignName, setCampaignName] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [hour,   setHour]               = useState("09");
  const [minute, setMinute]             = useState("00");
  const [ampm,   setAmpm]               = useState("AM");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form whenever modal opens
  useEffect(() => {
    if (isOpen) {
      setCampaignName("");
      setSelectedDate(null);
      setHour("09");
      setMinute("00");
      setAmpm("AM");
    }
  }, [isOpen]);

  const isValid =
    campaignName.trim().length > 0 &&
    selectedDate !== null &&
    selectedGroups.length > 0 &&
    !!selectedSenderId &&
    message.trim().length > 0;

  const scheduledAt = selectedDate ? formatScheduledAt(selectedDate, hour, minute, ampm) : null;
  const displayDate = selectedDate
    ? selectedDate.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })
    : "Pick a date";
  const displayTime = `${hour}:${minute} ${ampm}`;

  const hints = [
    !campaignName.trim()        && "Campaign name",
    !selectedDate               && "Date",
    !selectedSenderId           && "Sender ID",
    selectedGroups.length === 0 && "Contact group",
    !message.trim()             && "Message",
  ].filter(Boolean);

  const handleSchedule = async () => {
    if (!isValid) return;
    setIsSubmitting(true);


    try {
      const token = localStorage.getItem("txtconnect_ng_user_token") ||
                    localStorage.getItem("access_token") ||
                    localStorage.getItem("txtconnect_token") || "";

      const payload = {
        campaign_name:       campaignName.trim(),
        sender_id:           selectedSenderId,
        content:             message,
        status:              "scheduled",
        scheduled_at:        scheduledAt,             // "YYYY-MM-DD HH:00:00"
        contact_group_slugs: selectedGroups.map((g) => g.contact_group_slug),
      };

      const response = await axios.post(
        `${BASE_URL}api/user/create/campaign`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const resData = response.data;
      if (
        resData?.status_code === "000" ||
        resData?.status_code === "200" ||
        response.status === 200 ||
        response.status === 201
      ) {
        toast.success(
          `Campaign "${campaignName}" scheduled for ${displayDate} at ${displayTime}!`
        );
        onClose();
        window.location.reload();
      } else {
        toast.error(resData?.reason || resData?.message || "Failed to schedule campaign");
      }
    } catch (err) {
  console.error("Schedule campaign error:", err);

  const msg =
    err?.response?.data?.data?.errors?.scheduled_at?.[0] ||
    err?.response?.data?.data?.reason ||
    err?.response?.data?.data?.message ||
    "An error occurred. Please try again.";

  toast.error(msg);
}
  };

  

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm bg-black/40"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 24 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* ── Header ── */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-red-50 to-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-xl border border-red-200">
                  <Calendar className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-800">Schedule Campaign</h2>
                  <p className="text-xs text-gray-500">Set a future date &amp; time to send</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* ── Body ── */}
            <div className="px-6 py-5 space-y-5 max-h-[75vh] overflow-y-auto">

              {/* Campaign Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                  Campaign Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="e.g. March Promo Campaign"
                  maxLength={80}
                  className="w-full px-4 py-2.5 text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent placeholder-gray-400 transition-all"
                />
                <div className="text-right text-[10px] text-gray-400 mt-1">{campaignName.length}/80</div>
              </div>

              {/* Read-only summary */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wide mb-0.5">Sender ID</div>
                  <div className="text-sm font-semibold text-gray-800 truncate">
                    {selectedSenderId || <span className="text-red-400 font-normal">Not selected</span>}
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wide mb-0.5">Groups · Contacts</div>
                  <div className="text-sm font-semibold text-gray-800">
                    {selectedGroups.length} group{selectedGroups.length !== 1 ? "s" : ""} ·{" "}
                    {selectedGroups.reduce((s, g) => s + (g.number_of_contacts || 0), 0).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  <Calendar className="w-3.5 h-3.5 text-red-500" /> Date <span className="text-red-500">*</span>
                </label>
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <MiniCalendar selectedDate={selectedDate} onChange={setSelectedDate} minDate={today} />
                </div>
                <AnimatePresence>
                  {selectedDate && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      className="mt-2 flex items-center gap-2 text-xs text-red-600 font-medium"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> {displayDate}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Time */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  <Clock className="w-3.5 h-3.5 text-red-500" /> Time <span className="text-red-500">*</span>
                </label>
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <TimePicker
                    hour={hour}
                    minute={minute}
                    ampm={ampm}
                    onHourChange={setHour}
                    onMinuteChange={setMinute}
                    onAmpmChange={setAmpm}
                  />
                </div>
                <p className="mt-2 text-xs text-center text-gray-500">
                  Selected: <span className="font-semibold text-gray-700">{displayTime}</span>
                </p>
              </div>

              {/* Ready banner */}
              <AnimatePresence>
                {isValid && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="p-3 rounded-xl bg-green-50 border border-green-200 flex items-start gap-2"
                  >
                    <CheckCircle2 className="w-4 h-4 text-green-700 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-green-700">
                      <span className="font-semibold">Ready:</span> "{campaignName}" will send on{" "}
                      <strong>{displayDate}</strong> at <strong>{displayTime}</strong>
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Hints */}
              {hints.length > 0 && (
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    Still needed: <span className="font-semibold">{hints.join(", ")}</span>
                  </p>
                </div>
              )}
            </div>

            {/* ── Footer ── */}
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3 bg-gray-50/50">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <motion.button
                onClick={handleSchedule}
                disabled={!isValid || isSubmitting}
                whileHover={isValid && !isSubmitting ? { scale: 1.02 } : {}}
                whileTap={isValid && !isSubmitting ? { scale: 0.97 } : {}}
                className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-xl transition-all shadow-sm ${
                  isValid && !isSubmitting
                    ? "bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-red-200"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Scheduling...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Schedule Campaign
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ScheduleCampaignModal;